import { BlockConfig, BlockConfigWithChildren } from '../core/types'

export const nestBlockConfigs = (blockConfigs: BlockConfig[]): BlockConfig[] => {
    const configMap = buildConfigMap(blockConfigs)
    const roots = buildParentChildRelationships(blockConfigs, configMap)
    reorderChildrenByType(configMap)
    return roots
}

const buildConfigMap = (
    blockConfigs: BlockConfigWithChildren[]
): Map<string, BlockConfigWithChildren> => {
    const configMap = new Map<string, BlockConfigWithChildren>()
    for (const config of blockConfigs) {
        configMap.set(config.id, { ...config, children: [] })
    }
    return configMap
}

const buildParentChildRelationships = (
    blockConfigs: BlockConfigWithChildren[],
    configMap: Map<string, BlockConfigWithChildren>
): BlockConfigWithChildren[] => {
    const roots: BlockConfigWithChildren[] = []

    for (const config of blockConfigs) {
        const current = configMap.get(config.id)!
        if (config.parentId) {
            const parent = configMap.get(config.parentId)
            if (parent) {
                parent.children!.push(current)
            }
        } else {
            roots.push(current)
        }
    }

    return roots
}

const reorderChildrenByType = (configMap: Map<string, BlockConfigWithChildren>): void => {
    for (const config of configMap.values()) {
        if (!config.children?.length) continue

        if (config.type === 'container' || config.type === 'list') {
            const orderedIds = config.properties.childBlockConfigIds
            if (!orderedIds.length) {
                config.children = []
                continue
            }

            const childMap = new Map(config.children!.map(c => [c.id, c]))
            config.children = orderedIds
                .map(id => childMap.get(id))
                .filter((c): c is BlockConfig => Boolean(c))
        } else if (config.type === 'grid' || config.type === 'tabs') {
            const items = config.properties.items || []
            if (!items.length) {
                config.children = []
                continue
            }
            const orderedIds: string[] = []
            for (const item of items) {
                if (item.childBlockConfigIds) {
                    orderedIds.push(...item.childBlockConfigIds)
                }
            }
            if (!orderedIds.length) {
                config.children = []
                continue
            }

            const childMap = new Map(config.children!.map(c => [c.id, c]))
            config.children = orderedIds
                .map(id => childMap.get(id))
                .filter((c): c is BlockConfig => Boolean(c))
        } else {
            throw new Error(
                `Block type "${config.type}" has children but needs to be reordered based on childBlockConfigIds.`
            )
        }
    }
}
